/* istanbul ignore file */
import React, { useMemo, useState } from 'react';
import { Grid2 as Grid } from '@mui/material';
import RemoveListItemButton from 'domain/projects/project/weeklyreports/shared/button/RemoveListItemButton';
import { AvailableOnProdLabels, WasDemoedLabels, WorkItemStatusLabels } from '../../ProjectStatusesService';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { TextButton } from 'framework/ui/ui-builder';
import { EnumDropdown } from 'framework/form';
import TicketAssigneeSearchField from './TicketAssigneeSearchField';
import { Box } from '@mui/material';

export type DeliveredWorkItemType = {
  id: number;
  ticketName: string;
  fromPlanning: boolean;
  assigneeName: string;
  assigneeAlias: string;
  customerFeedback: string;
  comments: string;
  wasDemoed: string;
  availableOnProd: string;
  status: string;
};

interface DeliveredWorkItemInputRowProps {
  data: DeliveredWorkItemType;
  id: number;
  formData: any;
  updateTickets: (newFormData: any) => void;
  getErrorMessages: (fieldSource: string) => string[];
  disabled?: boolean;
}

const DeliveredWorkItemInputRow = ({
  data,
  id,
  formData,
  getErrorMessages,
  updateTickets,
  disabled = false,
}: DeliveredWorkItemInputRowProps) => {
  const [rowData, setRowData] = useState(data);
  const source = `tickets[${id}]`;
  const enumErrorSource = `tickets.${id}`;
  const projectName = formData.project?.name;

  const onChangeFieldCallback = (fieldName: string) => (newFormData: FormFieldsData) => {
    const newData = {
      ...rowData,
      assigneeAlias: data.assigneeAlias,
      assigneeName: data.assigneeName,
      [`${fieldName}`]: newFormData[`${source}.${fieldName}`],
    };

    setRowData({ ...newData });
    const listData = formData.tickets;
    listData[id] = { ...newData };

    updateTickets(listData);
  };

  const removeRow = () => {
    const listData = formData.tickets;
    listData.splice(id, 1);

    updateTickets(listData);
  };

  const isDisabledSpecific = useMemo(() => {
    return rowData.fromPlanning;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData.fromPlanning, rowData.id]);

  return (
    <Box display="flex" gap={2} alignItems="flex-start" mb={2}>
      <Grid container spacing={2} sx={{ flex: 1 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextButton
            source={`${source}.ticketName`}
            label={id === 0 ? 'Work item (name or link)' : ''}
            value={rowData.ticketName}
            disabled={isDisabledSpecific || disabled}
            onChangeCallback={onChangeFieldCallback('ticketName')}
            isRequired
            title="Work item (name or link)"
            errors={getErrorMessages(`${source}.ticketName`)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <TicketAssigneeSearchField
            isRequired
            isDisabled={(rowData.assigneeAlias === null && rowData.assigneeName !== null) || disabled}
            label={id === 0 ? 'Assignee' : ''}
            sourceAlias={`${enumErrorSource}.assigneeAlias`}
            nameSource={`${enumErrorSource}.assigneeName`}
            projectName={projectName}
            errors={getErrorMessages(`${source}.assignee`)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <TextButton
            source={`${source}.customerFeedback`}
            label={id === 0 ? 'Customer feedback' : ''}
            value={rowData.customerFeedback}
            onChangeCallback={onChangeFieldCallback('customerFeedback')}
            title="Customer feedback"
            errors={getErrorMessages(`${source}.customerFeedback`)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <TextButton
            source={`${source}.comments`}
            label={id === 0 ? 'Comments' : ''}
            value={rowData.comments}
            onChangeCallback={onChangeFieldCallback('comments')}
            errors={getErrorMessages(`${source}.comments`)}
            title="Comments"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <EnumDropdown
            disabled={disabled}
            source={`${source}.wasDemoed`}
            errorSource={`${enumErrorSource}.wasDemoed`}
            label={id === 0 ? 'Was demoed?' : ''}
            value={rowData.wasDemoed}
            isRequired
            onChangeCallback={onChangeFieldCallback('wasDemoed')}
            options={WasDemoedLabels}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <EnumDropdown
            disabled={disabled}
            source={`${source}.availableOnProd`}
            label={id === 0 ? 'Available on prod' : ''}
            value={rowData.availableOnProd}
            isRequired
            errorSource={`${enumErrorSource}.availableOnProd`}
            onChangeCallback={onChangeFieldCallback('availableOnProd')}
            options={AvailableOnProdLabels}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <EnumDropdown
            disabled={disabled}
            source={`${source}.status`}
            errorSource={`${enumErrorSource}.status`}
            label={id === 0 ? 'Status' : ''}
            value={rowData.status}
            isRequired
            onChangeCallback={onChangeFieldCallback('status')}
            options={WorkItemStatusLabels}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} />
      </Grid>

      {!disabled && (
        <Box>
          <RemoveListItemButton isVisible={!rowData.fromPlanning} onClickCallback={removeRow} sx={{ mb: 3 }} />
        </Box>
      )}
    </Box>
  );
};

export default DeliveredWorkItemInputRow;
